import ApiEndPoints from "@/constants/apiEndpoints";
import api from "@/lib/api";
import client from "@/lib/api/client";
import { dedup } from "@/lib/api/dedup";
import { csrfHeaders, handledErrorConfig, idempotencyHeaders } from "@/lib/api/headers";
import type {
  ICatalogResult,
  ICheckoutRequest,
  IInvoicesResponse,
  IInvoicesResult,
  IMyBillingSummary,
  IOrder,
  IOrdersResponse,
  IOrdersResult,
  IPaymentGatewayInfo,
  IPaymentGatewaysResult,
  IQuoteInvoiceData,
  IQuoteInvoiceSaveRequest,
  IQuoteRequest,
  IQuoteResult,
  ISubscription,
  ISubscriptionsResponse,
  ISubscriptionsResult,
  IValidateDiscountRequest,
  IValidateDiscountResult,
  IWalletEntriesResponse,
  IWalletEntriesResult,
  IWalletTransferDestination,
  IWalletTransferResource,
  IWalletTransfersResponse,
  IWalletTransfersResult,
  ICreateWalletTransferPayload,
  IWalletSpendingCap,
  IWalletAutoTopupSetting,
  IWalletAggregates,
  IUpdateWalletCapsPayload,
  IUpdateAutoTopupPayload,
} from "@/types/billing";
import type { IApiEnvelope } from "@/types/common";

export const ORDERS_PAGE_SIZE = 15;

export const SUBSCRIPTIONS_PAGE_SIZE = 15;

export const INVOICES_PAGE_SIZE = 15;

export const WALLET_ENTRIES_PAGE_SIZE = 20;

export const WALLET_TRANSFERS_PAGE_SIZE = 10;

const EMPTY_ORDERS_META = {
  current_page: 1,
  from: null,
  last_page: 1,
  per_page: ORDERS_PAGE_SIZE,
  to: null,
  total: 0,
};

const EMPTY_SUBSCRIPTIONS_META = {
  current_page: 1,
  from: null,
  last_page: 1,
  per_page: SUBSCRIPTIONS_PAGE_SIZE,
  to: null,
  total: 0,
};

const EMPTY_INVOICES_META = {
  current_page: 1,
  from: null,
  last_page: 1,
  per_page: INVOICES_PAGE_SIZE,
  to: null,
  total: 0,
};

const EMPTY_WALLET_ENTRIES_META = {
  current_page: 1,
  from: null,
  last_page: 1,
  per_page: WALLET_ENTRIES_PAGE_SIZE,
  to: null,
  total: 0,
};

const EMPTY_WALLET_TRANSFERS_META = {
  current_page: 1,
  from: null,
  last_page: 1,
  per_page: WALLET_TRANSFERS_PAGE_SIZE,
  to: null,
  total: 0,
};

export function getCatalog(): Promise<ICatalogResult> {
  return dedup("billing/catalog", async () => {
    const payload = await api.get<IApiEnvelope<ICatalogResult>>(ApiEndPoints.BILLING.CATALOG);
    return payload.data!;
  });
}

export function getDefaultPaymentGateway(): Promise<IPaymentGatewayInfo | null> {
  return dedup("billing/payment-gateways", () =>
    api
      .get<IApiEnvelope<IPaymentGatewaysResult>>(ApiEndPoints.BILLING.PAYMENT_GATEWAYS)
      .then((payload) => payload.data?.default ?? null),
  );
}

export async function getQuote(data: IQuoteRequest): Promise<IQuoteResult> {
  const payload = await api.post<IApiEnvelope<IQuoteResult>>(
    ApiEndPoints.BILLING.QUOTE,
    data,
    handledErrorConfig,
  );
  return payload.data!;
}

export async function validateDiscount(
  data: IValidateDiscountRequest,
): Promise<IValidateDiscountResult> {
  const payload = await api.post<IApiEnvelope<IValidateDiscountResult>>(
    ApiEndPoints.BILLING.VALIDATE_DISCOUNT,
    data,
    handledErrorConfig,
  );
  return payload.data!;
}

export async function checkout(data: ICheckoutRequest, idempotencyKey: string): Promise<IOrder> {
  const payload = await api.post<IApiEnvelope<IOrder>>(ApiEndPoints.BILLING.CHECKOUT, data, {
    headers: { ...csrfHeaders(), ...idempotencyHeaders(idempotencyKey) },
    ...handledErrorConfig,
  });
  return payload.data!;
}

export function getOrder(id: string): Promise<IOrder> {
  return dedup(`billing/order:${id}`, async () => {
    const payload = await api.get<IApiEnvelope<IOrder>>(ApiEndPoints.BILLING.ORDER(id));
    return payload.data!;
  });
}

export async function verifyOrder(id: string): Promise<IOrder> {
  const payload = await api.post<IApiEnvelope<IOrder>>(
    ApiEndPoints.BILLING.ORDER_VERIFY(id),
    undefined,
    { headers: csrfHeaders(), ...handledErrorConfig },
  );
  return payload.data!;
}

export async function failOrder(id: string, reason?: string): Promise<IOrder> {
  const payload = await api.post<IApiEnvelope<IOrder>>(
    ApiEndPoints.BILLING.ORDER_FAIL(id),
    reason ? { reason } : undefined,
    { headers: csrfHeaders(), ...handledErrorConfig },
  );
  return payload.data!;
}

export async function downloadOrderReceipt(id: string): Promise<void> {
  const response = await client.get(ApiEndPoints.BILLING.ORDER_RECEIPT(id), {
    responseType: "blob",
  });

  const disposition = (response.headers["content-disposition"] as string | undefined) ?? "";
  const fileNameMatch = disposition.match(/filename="?([^";]+)"?/);
  const fileName = fileNameMatch?.[1] ?? "trendi-receipt.pdf";

  const url = URL.createObjectURL(response.data as Blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export async function startTrial(): Promise<ISubscription> {
  const payload = await api.post<IApiEnvelope<ISubscription>>(
    ApiEndPoints.BILLING.TRIAL_START,
    undefined,
    { headers: csrfHeaders() },
  );
  return payload.data!;
}

export function getMyBilling(): Promise<IMyBillingSummary> {
  return dedup("me/billing", () =>
    api
      .get<IApiEnvelope<IMyBillingSummary>>(ApiEndPoints.ME_BILLING.SUMMARY)
      .then((payload) => payload.data!),
  );
}

export function getMyOrders(params?: {
  page?: number;
  per_page?: number;
}): Promise<IOrdersResult> {
  const key = `me/orders?${JSON.stringify(params ?? {})}`;
  return dedup(key, async () => {
    const payload = await api.get<IOrdersResponse>(ApiEndPoints.ME_BILLING.ORDERS, { params });
    return { orders: payload.data ?? [], meta: payload.meta ?? EMPTY_ORDERS_META };
  });
}

export function getMySubscriptions(params?: {
  page?: number;
  per_page?: number;
}): Promise<ISubscriptionsResult> {
  const key = `me/subscriptions?${JSON.stringify(params ?? {})}`;
  return dedup(key, async () => {
    const payload = await api.get<ISubscriptionsResponse>(ApiEndPoints.ME_BILLING.SUBSCRIPTIONS, {
      params,
    });
    return { subscriptions: payload.data ?? [], meta: payload.meta ?? EMPTY_SUBSCRIPTIONS_META };
  });
}

export function getMyInvoices(params?: {
  page?: number;
  per_page?: number;
}): Promise<IInvoicesResult> {
  const key = `me/invoices?${JSON.stringify(params ?? {})}`;
  return dedup(key, async () => {
    const payload = await api.get<IInvoicesResponse>(ApiEndPoints.ME_BILLING.INVOICES, { params });
    return { invoices: payload.data ?? [], meta: payload.meta ?? EMPTY_INVOICES_META };
  });
}

export function getMyWalletEntries(params?: {
  page?: number;
  per_page?: number;
}): Promise<IWalletEntriesResult> {
  const key = `me/wallet/entries?${JSON.stringify(params ?? {})}`;
  return dedup(key, async () => {
    const payload = await api.get<IWalletEntriesResponse>(ApiEndPoints.ME_BILLING.WALLET_ENTRIES, {
      params,
    });
    return { entries: payload.data ?? [], meta: payload.meta ?? EMPTY_WALLET_ENTRIES_META };
  });
}

/*
export async function getMyWalletWithdraws(params?: {
  page?: number;
  per_page?: number;
}): Promise<IWalletWithdrawsResult> {
  const payload = await api.get<IWalletWithdrawsResponse>(
    ApiEndPoints.ME_BILLING.WALLET_WITHDRAWS,
    {
      params,
    },
  );
  return { withdraws: payload.data ?? [], meta: payload.meta ?? EMPTY_WALLET_ENTRIES_META };
}

export async function createWalletWithdraw(
  data: ICreateWalletWithdrawPayload,
): Promise<IWalletWithdrawRequest> {
  const payload = await api.post<IApiEnvelope<IWalletWithdrawRequest>>(
    ApiEndPoints.ME_BILLING.WALLET_WITHDRAWS,
    data,
    { headers: csrfHeaders(), ...handledErrorConfig },
  );
  return payload.data!;
}
*/

export async function getMyWalletTransfers(params?: {
  page?: number;
  per_page?: number;
}): Promise<IWalletTransfersResult> {
  const payload = await api.get<IWalletTransfersResponse>(
    ApiEndPoints.ME_BILLING.WALLET_TRANSFERS,
    {
      params,
    },
  );
  return { transfers: payload.data ?? [], meta: payload.meta ?? EMPTY_WALLET_TRANSFERS_META };
}

export async function getWalletTransferDestinations(): Promise<IWalletTransferDestination[]> {
  const payload = await api.get<IApiEnvelope<IWalletTransferDestination[]>>(
    ApiEndPoints.ME_BILLING.WALLET_TRANSFER_DESTINATIONS,
  );
  return payload.data ?? [];
}

export async function createWalletTransfer(
  data: ICreateWalletTransferPayload,
): Promise<IWalletTransferResource> {
  const payload = await api.post<IApiEnvelope<IWalletTransferResource>>(
    ApiEndPoints.ME_BILLING.WALLET_TRANSFERS,
    {
      to_wallet_id: data.to_wallet_id,
      amount: data.amount,
      note: data.note ?? null,
      idempotency_key: data.idempotency_key,
    },
    {
      headers: {
        ...csrfHeaders(),
        ...idempotencyHeaders(data.idempotency_key),
      },
      ...handledErrorConfig,
    },
  );
  return payload.data!;
}

export async function chargeWallet(amount: number): Promise<IOrder> {
  const payload = await api.post<IApiEnvelope<IOrder>>(
    "/api/v1/wallet/charge",
    { amount },
    {
      headers: { ...csrfHeaders(), ...idempotencyHeaders(crypto.randomUUID()) },
      ...handledErrorConfig,
    },
  );
  return payload.data!;
}

export function getMyWalletCaps(): Promise<IWalletSpendingCap> {
  return dedup("me/wallet/caps", async () => {
    const payload = await api.get<IApiEnvelope<IWalletSpendingCap>>(
      ApiEndPoints.ME_BILLING.WALLET_CAPS,
    );
    return payload.data!;
  });
}

export async function updateMyWalletCaps(
  data: IUpdateWalletCapsPayload,
): Promise<IWalletSpendingCap> {
  const payload = await api.put<IApiEnvelope<IWalletSpendingCap>>(
    ApiEndPoints.ME_BILLING.WALLET_CAPS,
    data,
    { headers: csrfHeaders(), ...handledErrorConfig },
  );
  return payload.data!;
}

export function getMyAutoTopup(): Promise<IWalletAutoTopupSetting> {
  return dedup("me/wallet/auto-topup", async () => {
    const payload = await api.get<IApiEnvelope<IWalletAutoTopupSetting>>(
      ApiEndPoints.ME_BILLING.WALLET_AUTO_TOPUP,
    );
    return payload.data!;
  });
}

export async function updateMyAutoTopup(
  data: IUpdateAutoTopupPayload,
): Promise<IWalletAutoTopupSetting> {
  const payload = await api.put<IApiEnvelope<IWalletAutoTopupSetting>>(
    ApiEndPoints.ME_BILLING.WALLET_AUTO_TOPUP,
    data,
    { headers: csrfHeaders(), ...handledErrorConfig },
  );
  return payload.data!;
}

export function getWalletAggregates(): Promise<IWalletAggregates> {
  return dedup("me/wallet/aggregates", async () => {
    const payload = await api.get<IApiEnvelope<IWalletAggregates>>(
      ApiEndPoints.ME_BILLING.WALLET_AGGREGATES,
    );
    return payload.data!;
  });
}

export async function saveQuoteInvoice(data: IQuoteInvoiceSaveRequest): Promise<IQuoteInvoiceData> {
  const payload = await api.post<IApiEnvelope<IQuoteInvoiceData>>(
    ApiEndPoints.BILLING.QUOTE_INVOICE,
    data,
    { ...handledErrorConfig },
  );
  return payload.data!;
}

export async function getQuoteInvoice(): Promise<IQuoteInvoiceData | null> {
  const payload = await api.get<IApiEnvelope<IQuoteInvoiceData | null>>(
    ApiEndPoints.BILLING.QUOTE_INVOICE,
  );
  return payload.data ?? null;
}

export async function clearQuoteInvoice(): Promise<void> {
  await api.delete<IApiEnvelope<null>>(ApiEndPoints.BILLING.QUOTE_INVOICE, {
    ...handledErrorConfig,
  });
}
